import type { RefreshTokenStore } from './types.ts'

/**
 * The refresh token's homes. The access token has none — it lives in a closure in
 * session.ts and is never handed to any of these.
 */

const REFRESH_TOKEN_KEY = 'volee.auth.refresh'

/**
 * The safe default, and what tests use: nothing survives a reload.
 *
 * Chosen as the default deliberately — a caller who never thinks about storage gets the
 * conservative behaviour (re-login after refresh) rather than a token on disk.
 */
export class MemoryRefreshTokenStore implements RefreshTokenStore {
  #token: string | null = null

  read(): Promise<string | null> {
    return Promise.resolve(this.#token)
  }

  write(token: string): Promise<void> {
    this.#token = token
    return Promise.resolve()
  }

  clear(): Promise<void> {
    this.#token = null
    return Promise.resolve()
  }
}

/** Web implementation: refresh token in `localStorage`, so a reload does not log the user out. It
 * depends on refresh token rotation being on in the pool:
 * packages/auth/README.md § «Le implementazioni di storage». */
export class LocalStorageRefreshTokenStore implements RefreshTokenStore {
  readonly #key: string

  constructor(key: string = REFRESH_TOKEN_KEY) {
    this.#key = key
  }

  read(): Promise<string | null> {
    return Promise.resolve(this.#storage()?.getItem(this.#key) ?? null)
  }

  write(token: string): Promise<void> {
    this.#storage()?.setItem(this.#key, token)
    return Promise.resolve()
  }

  clear(): Promise<void> {
    this.#storage()?.removeItem(this.#key)
    return Promise.resolve()
  }

  #storage(): Storage | null {
    // Throws in Safari private mode rather than merely being absent.
    try {
      return globalThis.localStorage ?? null
    } catch {
      return null
    }
  }
}

/**
 * Adapter for the native secure storage (iOS Keychain / Android Keystore) that arrives
 * with the Capacitor shell. The contract is declared now, per S4 of the scaffolding
 * spec; the implementation lands with the shell.
 *
 * Whoever writes it implements `RefreshTokenStore` over the plugin and passes it to
 * `createAuthClient` — nothing else in the package or in the apps changes. That is the
 * whole point of the interface.
 *
 * @example
 * ```ts
 * class KeychainRefreshTokenStore implements RefreshTokenStore {
 *   async read() { return (await SecureStorage.get({ key: KEY })).value ?? null }
 *   async write(token: string) { await SecureStorage.set({ key: KEY, value: token }) }
 *   async clear() { await SecureStorage.remove({ key: KEY }) }
 * }
 * ```
 */
export const NATIVE_REFRESH_TOKEN_KEY = REFRESH_TOKEN_KEY
